import { Input } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'

/**
 * A comma-separated list field that keeps its own draft text while focused.
 *
 * Splitting on every keystroke made the separator impossible to type: the moment a
 * comma was entered the value was re-derived and re-joined as `a, b`, so the caret
 * jumped and a trailing `,` never survived long enough to type a word after it.
 * Holding the raw text until blur means the field behaves like a text input, and the
 * array is derived once, when editing stops.
 *
 * The parent's value still wins whenever it changes from outside — a reset, a
 * discarded edit, a different variable in the same slot.
 */
export function CommaListInput({
	value,
	label,
	ariaLabel,
	placeholder,
	isDisabled,
	onChange,
}: {
	value: string[]
	label: string
	ariaLabel: string
	placeholder?: string
	isDisabled?: boolean
	onChange: (next: string[]) => void
}) {
	const joined = value.join(', ')
	const [draft, setDraft] = useState(joined)
	const [editing, setEditing] = useState(false)

	// Adopt the parent's value unless the user is mid-edit, which is what keeps an
	// outside reset visible without fighting the keystroke being typed.
	useEffect(() => {
		if (!editing) setDraft(joined)
	}, [joined, editing])

	const commit = (text: string) => {
		setEditing(false)
		onChange(
			text
				.split(',')
				.map((part) => part.trim())
				.filter(Boolean),
		)
	}

	return (
		<label className="block">
			<span className="mb-1 block text-ds-2xs text-ds-text-subtle">{label}</span>
			<Input
				value={draft}
				aria-label={ariaLabel}
				disabled={isDisabled}
				{...(placeholder === undefined ? {} : { placeholder })}
				onFocus={() => setEditing(true)}
				onChange={(e) => {
					setEditing(true)
					setDraft(e.target.value)
				}}
				onBlur={(e) => commit(e.target.value)}
			/>
		</label>
	)
}
